// My hours — the week, day by day, and the timesheet the week becomes.
//
// Typed hours are authoritative and the clock is evidence (REBUILD_API §0),
// so a line whose hours differ from the clock says so: an Adjusted pill, the
// reason, and "Clock said 8.25". Editing a line sends the WHOLE entries
// array — the server deletes any line it does not receive — so every line
// goes back with its id and hours, and only the edited one carries a change.
//
// That same replace-all shape is what makes ADDING and REMOVING a line
// possible with no new endpoint: a line sent without an id is inserted, and a
// line simply left out is deleted (hoursModel.applyLineEdits). Until
// 2026-09-08 this screen used neither, so a crew member could change a figure
// but could not record a job they worked without clocking, and could not take
// off a line the clock created by mistake — both had to go through the
// office, which is the thing the owner said the app should not need.

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Pill, FwButton, Notice, SectionLabel, EmptyState, Skeleton, Sheet, FwField, FwInput, FwSelect, FwTextarea, formatHours } from '../../../ui/fw';
// H:MM, the one reading every hours figure in this app uses. From
// apps/shared rather than dispatch/lib/format: a field screen reaching into
// another application's lib is the coupling this rebuild keeps refusing, and
// hoursPicker.test.js pins clockLabel equal to dispatch's hoursClock at every
// value the picker can produce.
import { clockLabel, sameHours } from '../../shared/hoursPicker';
import { HoursField } from '../../shared/HoursField';
import { Icon } from '../../../ui';
import { post, put, describeError } from '../../shared/http';
import { useHours, hoursKey, usePaystubs, usePayYear } from '../queries';
import { linePriceLabel, lineAmountLabel } from '../../../utils/timesheetPay';
import { useAuth } from '../../../App';
import { downloadBlob, paystubPdf } from '../../shared/download';
import { dayLabel, shortDate, rangeLabel, timeOf, durationLabel, weekBefore, weekAfter, whenLabel } from '../fmt';
import { addDaysISO } from '../../../utils/scheduleDates';

const STATUS_TONE = { draft: 'neutral', submitted: 'sched', approved: 'ok', rejected: 'stop' };
const STATUS_LABEL = { draft: 'Draft', submitted: 'Submitted', approved: 'Approved', rejected: 'Rejected' };
const PAY_TONE = { pending: 'sched', paid: 'ok' };
const PAY_LABEL = { pending: 'Pending', paid: 'Paid' };

export function StatusPill({ status }) {
  return <Pill tone={STATUS_TONE[status] || 'neutral'}>{STATUS_LABEL[status] || status || 'None'}</Pill>;
}

export function isAdjustedLine(line) {
  return !!(line && (line.adjusted || line.source === 'adjusted'));
}

/**
 * Every recorded change to THIS line's hours — collapsed until asked for.
 *
 * ── Why a worker needs this and did not have it ────────────────────────────
 *
 * `adjustment_reason`, `adjusted_by` and `adjusted_at` are SINGLE COLUMNS on
 * the line, so every adjustment overwrites the last. A line taken 8 → 6.5
 * ("timer ran through lunch") → 5.75 ("Marcus confirmed") showed this screen
 * 5.75 and "Marcus confirmed", and nothing anywhere said it had ever been 8.
 * On a disagreement about a week's pay the crew member had no way to see what
 * their hours were, who moved them, or why — while the APPROVER has had all
 * three on dispatch/screens/SheetDetail.js since 2026-09-08.
 *
 * The owner asked for exactly this, twice: "there should also history of
 * changes" (2026-09-08) and "Workers should be able to view and modify their
 * own timesheets!!!". A worker reading their own record is the same mechanism
 * as an admin reading it.
 *
 * `GET /api/field/hours` now attaches `history` to each line, oldest first
 * (server/routes/field.js) — the SAME array /api/timesheets/:id returns, which
 * fieldHoursHistory.test.js pins by comparing the two responses. It is not a
 * second request from the phone on purpose: docs/ONE_APP.md records a poll on
 * this app walking into the shared per-IP rate limiter, and a yard full of
 * crew is many phones behind one carrier NAT.
 *
 * ── Why collapsed, and why not inside the edit sheet ───────────────────────
 *
 * A line nobody has adjusted renders NOTHING — no heading, no empty state, no
 * control — so the ordinary week is unchanged. A line that has one costs a
 * single short label until somebody wants it, which is what stops a line
 * adjusted six times burying the five lines under it on a 320px screen.
 *
 * On the line rather than in the edit sheet, because the sheet does not open
 * on an APPROVED week (`editable` is false) and an approved week is precisely
 * when somebody asks how the figure was arrived at. It also renders
 * independently of `adjusted`: hours moved away from the clock and back again
 * leave a line that is no longer "adjusted" and still has a history worth
 * reading.
 */
export function LineHistory({ line }) {
  const changes = (line && Array.isArray(line.history)) ? line.history : [];
  const [open, setOpen] = useState(false);
  if (changes.length === 0) return null;
  return (
    <div className="fd-hist">
      <FwButton
        variant="quiet"
        icon={open ? 'chevronUp' : 'chevronDown'}
        aria-expanded={open ? 'true' : 'false'}
        data-history-toggle={String(line.id)}
        onClick={() => setOpen((v) => !v)}
      >
        {changes.length === 1 ? '1 change' : `${changes.length} changes`}
      </FwButton>
      {open ? changes.map((h, i) => (
        <div key={h.id != null ? h.id : i} data-history-row className="fd-hist__row">
          <span className="fd-hist__figures">
            <span className="fw-num">{clockLabel(h.from_hours)}</span>
            {' \u2192 '}
            <span className="fw-num">{clockLabel(h.to_hours)}</span>
          </span>
          <span className="fd-hist__who">
            {h.by || 'someone no longer on the roster'}
            {whenLabel(h.at) ? ` \u00b7 ${whenLabel(h.at)}` : ''}
          </span>
          <span className="fd-hist__reason">{h.reason || 'No reason given'}</span>
        </div>
      )) : null}
    </div>
  );
}

/** The full entries array for PUT /timesheets/:id with one line changed. */
export function entriesWithEdit(lines, editedId, hours, reason) {
  return lines.map((l) => {
    const base = { id: l.id, job_id: l.job_id, date: l.date, hours: l.hours };
    if (l.id !== editedId) return base;
    return { ...base, hours: Number(hours), adjustment_reason: reason || null };
  });
}

/** The full entries array for PUT /timesheets/:id with one line taken off. */
export function entriesWithout(lines, removedId) {
  return lines
    .filter((l) => l.id !== removedId)
    .map((l) => ({ id: l.id, job_id: l.job_id, date: l.date, hours: l.hours }));
}

/**
 * The full entries array with one new line appended. The new line carries no
 * `id`, which is what tells the server to insert it rather than update.
 * `custom_description` is what a line with no job is called on a paystub, so
 * a line is never nameless.
 */
export function entriesWithAdded(lines, added) {
  const base = lines.map((l) => ({ id: l.id, job_id: l.job_id, date: l.date, hours: l.hours }));
  return [...base, {
    job_id: added.job_id || null,
    date: added.date,
    hours: Number(added.hours),
    custom_description: added.job_id ? null : (added.custom_description || null),
    notes: added.notes || null,
  }];
}

/**
 * The jobs a crew member worked on one day, as one line under the date.
 *
 * A collapsed day row printed the date and a number and nothing else, so "was
 * that the Whitfield job or Brookline?" cost a tap per day, seven days a week,
 * on the screen somebody checks their pay on. The names are already in the
 * payload — `GET /api/field/hours` returns `days[].entries[].job_title` and
 * `property_name` — so this is a line of display, not a request.
 *
 * Deduplicated in the order first worked, and capped: three or four jobs in a
 * day is normal here and printing them all would wrap the row to five lines on
 * a 320px phone, which is the starved-container symptom docs/APP_REBUILD.md
 * §5.6 measures. An entry with neither a job nor a property is skipped rather
 * than rendered as a blank — and a day of only those returns null, so the row
 * is the date and the hours exactly as before.
 */
export function dayJobsLabel(entries, max = 2) {
  const names = [];
  for (const e of entries || []) {
    const name = (e && (e.job_title || e.property_name)) || null;
    if (name && !names.includes(name)) names.push(name);
  }
  if (names.length === 0) return null;
  if (names.length <= max) return names.join(' · ');
  return `${names.slice(0, max).join(' · ')} +${names.length - max} more`;
}

/** The seven dates of the week, so a new line can only land inside it. */
export function weekDates(startDate) {
  if (!startDate) return [];
  return Array.from({ length: 7 }, (_, i) => addDaysISO(startDate, i));
}

/**
 * What a worker can attach a new line to, without asking the server for a job
 * list it has no endpoint for: every job they clocked this week, plus every
 * job already on the sheet. Deduplicated by id, in the order first seen.
 * Anything else is typed as a description — the honest fallback for work done
 * off the clock, not a missing feature pretending to be one.
 */
export function weekJobOptions(days, lines) {
  const seen = new Map();
  for (const d of days || []) {
    for (const e of d.entries || []) {
      if (e.job_id && !seen.has(e.job_id)) {
        seen.set(e.job_id, { job_id: e.job_id, label: e.job_title || `Job #${e.job_id}` });
      }
    }
  }
  for (const l of lines || []) {
    if (l.job_id && !seen.has(l.job_id)) {
      seen.set(l.job_id, { job_id: l.job_id, label: l.job_title || `Job #${l.job_id}` });
    }
  }
  return [...seen.values()];
}

// Display only. Every figure below is one the server sent — a paystub's
// columns in dollars, or the year's sums in cents — and nothing here adds
// money up: "the paystubs come from the timesheets" (owner, 2026-10-09), and
// a second sum on the phone is how two figures for one thing start to differ.
const USD = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const money = (v) => {
  const n = Number(v);
  return v === null || v === undefined || v === '' || !Number.isFinite(n) ? '—' : USD.format(n);
};
const moneyCents = (c) => money(c === null || c === undefined ? c : Number(c) / 100);
const percent = (rate) => `${Math.round(Number(rate) * 1000) / 10}%`;

const YearRow = ({ label, figure, name, total = false }) => (
  <div className={`fd-payyear__row${total ? ' fd-payyear__row--total' : ''}`}>
    <dt>{label}</dt>
    <dd className="fw-num" data-figure={name}>{figure}</dd>
  </div>
);

/**
 * What the worker was paid in a year, and a rough tax set-aside — classic
 * "My Paystubs", restored 2026-10-09. Not classic's arithmetic: the year is
 * the year the money was PAID (the 1099's year), the set-aside is on pay for
 * the work (reimbursements out), and an employee gets none. The server says
 * which (utils/form1099.js workerYear).
 *
 * On a failure the figures are WITHHELD, never shown as $0.00: classic's
 * year card read "$0.00 gross" on an expired session, the same characters as
 * a worker who earned nothing (docs/AGENT_LEARNINGS.md, WorkerPaystubs).
 */
export function PayYear() {
  const [year, setYear] = useState(null);
  const q = usePayYear(year);
  const d = q.data;
  const sa = d && d.set_aside;
  return (
    <div className="fd-payyear" data-section="pay-year">
      <div className="fd-payyear__head">
        <SectionLabel>{d ? `Paid in ${d.year}` : 'Paid this year'}</SectionLabel>
        {d && Array.isArray(d.years) && d.years.length > 1 ? (
          <FwField label="Year" htmlFor="fd-pay-year">
            <FwSelect id="fd-pay-year" value={String(year || d.year)} onChange={(e) => setYear(Number(e.target.value))} aria-busy={q.isPlaceholderData || undefined}>
              {d.years.map((y) => <option key={y} value={String(y)}>{y}</option>)}
            </FwSelect>
          </FwField>
        ) : null}
      </div>
      {q.isPending ? <Skeleton lines={2} /> : null}
      {q.error ? <Notice tone="stop">{describeError(q.error, 'Could not work out what you were paid this year.')}</Notice> : null}
      {/* Nothing paid in the year is a sentence, not a row of $0.00 — the
          same "a slot spent saying nothing" rule as DocumentSummary's Paid. */}
      {d && d.paystubs === 0 ? <div className="fd-hint" data-pay-year-empty>Nothing was paid to you in {d.year}.</div> : null}
      {d && d.paystubs > 0 ? (
        <dl className="fd-payyear__rows">
          <YearRow label="Paid (before deductions)" figure={moneyCents(d.gross)} name="gross" />
          {d.reimbursements > 0 ? <YearRow label="Of that, reimbursements" figure={moneyCents(d.reimbursements)} name="reimbursements" /> : null}
          <YearRow label="Take-home" figure={moneyCents(d.net)} name="net" />
          <YearRow label="Paystubs paid" figure={String(d.paystubs)} name="paystubs" />
        </dl>
      ) : null}
      {d ? (
        <>
          {d.undated > 0 ? (
            <Notice tone="warn">
              {d.undated === 1 ? 'One paid paystub has' : `${d.undated} paid paystubs have`} no paid date, so {d.undated === 1 ? 'it is' : 'they are'} not in any year here. Ask the office to set the date.
            </Notice>
          ) : null}
          {sa && d.compensation > 0 ? (
            <div className="fd-payyear__setaside" data-section="set-aside">
              <SectionLabel>Tax to set aside</SectionLabel>
              <p className="fd-hint">
                {sa.basis === 'contractor'
                  ? 'You are paid as a 1099 contractor, so no tax is taken out of your pay.'
                  : 'If you are paid as a 1099 contractor, no tax is taken out of your pay.'}
                {' '}A rough amount to put aside on the {moneyCents(d.compensation)} paid for your work in {d.year}
                {d.reimbursements > 0 ? ' (reimbursements left out)' : ''}:
              </p>
              <dl className="fd-payyear__rows">
                <YearRow label={`Self-employment (${percent(sa.rates.self_employment)})`} figure={moneyCents(sa.self_employment)} name="self_employment" />
                {/* Classic's formula: 12% of what is left after half the
                    self-employment tax, so the label says so — a worker who
                    checks $2,350 × 12% gets $282, not the $260.43 shown. */}
                <YearRow label={`Federal (about ${percent(sa.rates.federal)}, after half the self-employment tax)`} figure={moneyCents(sa.federal)} name="federal" />
                <YearRow label={`Connecticut (about ${percent(sa.rates.state)})`} figure={moneyCents(sa.state)} name="state" />
                <YearRow label="Total to set aside" figure={moneyCents(sa.total)} name="total" total />
                <YearRow label="Each quarter" figure={moneyCents(sa.quarterly)} name="quarterly" />
                {sa.per_paystub != null ? <YearRow label="Per paystub" figure={moneyCents(sa.per_paystub)} name="per_paystub" /> : null}
              </dl>
              <p className="fd-hint">An estimate only, not tax advice. Check with a tax professional for what you actually owe.</p>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

/** The worker's own paystubs, newest first, each with its figures and its PDF. */
export function Paystubs({ userId, userName, download = downloadBlob }) {
  const stubs = usePaystubs();
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const rows = Array.isArray(stubs.data) ? [...stubs.data].sort((a, b) => String(b.pay_period_start).localeCompare(String(a.pay_period_start))) : [];
  const pdf = async (p) => {
    setBusy(p.id);
    setError(null);
    try {
      const { path, filename } = paystubPdf(userId, p, userName);
      await download(path, filename);
    } catch (e) {
      setError(describeError(e, 'Could not fetch that paystub.'));
    } finally {
      setBusy(null);
    }
  };
  return (
    <div className="fd-section" data-section="paystubs">
      <SectionLabel>Paystubs</SectionLabel>
      <PayYear />
      {error ? <Notice tone="stop" onDismiss={() => setError(null)}>{error}</Notice> : null}
      {stubs.isPending ? <Skeleton lines={2} /> : null}
      {stubs.error ? <Notice tone="stop">{describeError(stubs.error, 'Could not load your paystubs.')}</Notice> : null}
      {stubs.data && rows.length === 0 ? <div className="fd-hint">No paystubs yet. One is made when a timesheet is approved.</div> : null}
      <div className="fd-list">
        {rows.map((p) => (
          <div key={p.id} className="fd-line" data-paystub-id={p.id}>
            <span className="fd-line__main">
              <span className="fd-line__job">{rangeLabel(p.pay_period_start, p.pay_period_end)}</span>
              <span className="fd-line__meta">
                {/* DECIMAL here on purpose, and the only hours figure on this
                    screen that is. The button beside it downloads a PDF that
                    prints hours_worked with toFixed(2) (routes/timeEntries.js),
                    and a row reading 8:15 next to a document reading 8.25 is
                    this whole change's complaint, one screen over. Changing the
                    PDF is a money document and a separate human call, so the
                    row matches the document it links to. */}
                <span className="fw-num">{formatHours(p.hours_worked)} hours</span>
                {' · '}
                <Pill tone={PAY_TONE[p.status] || 'neutral'}>{PAY_LABEL[p.status] || p.status || 'Pending'}</Pill>
              </span>
              {/* The paystub's own columns, as stored — the same figures its
                  PDF prints. Deductions only when there are any. */}
              <span className="fd-line__price" data-stub-figures>
                {money(p.gross_pay)} gross
                {Number(p.deductions) > 0 ? ` · ${money(p.deductions)} deducted` : ''}
                {' · '}{money(p.net_pay)} take-home
              </span>
            </span>
            <FwButton variant="ghost" icon="download" onClick={() => pdf(p)} loading={busy === p.id} aria-label={`Download paystub for ${rangeLabel(p.pay_period_start, p.pay_period_end)}`}>PDF</FwButton>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Hours() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [week, setWeek] = useState(null);
  const hours = useHours(week);
  const [openDay, setOpenDay] = useState(null);
  const [edit, setEdit] = useState(null);
  // A NUMBER, not a decimal string. HoursField takes decimal hours and hands
  // decimal hours back; the person picks hours and minutes and never sees
  // either. `null` for "no line open" so 0 hours is still a real value.
  const [editHours, setEditHours] = useState(null);
  const [editReason, setEditReason] = useState('');
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [adding, setAdding] = useState(false);
  const [addDate, setAddDate] = useState('');
  const [addJob, setAddJob] = useState('');
  const [addWhat, setAddWhat] = useState('');
  const [addHours, setAddHours] = useState('');
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const data = hours.data;
  const sheet = data && data.timesheet;
  const lines = (sheet && sheet.lines) || [];
  const editable = !!sheet && sheet.status !== 'approved';

  const refresh = () => qc.invalidateQueries({ queryKey: hoursKey(week) });
  const run = async (label, fn, okText) => {
    setBusy(label);
    setError(null);
    setNotice(null);
    try {
      await fn();
      refresh();
      if (okText) setNotice(okText);
      return true;
    } catch (e) {
      setError(describeError(e, 'That did not go through'));
      return false;
    } finally {
      setBusy(null);
    }
  };

  /**
   * Build the week's sheet from the clock, or TOP UP the one that exists.
   *
   * One endpoint, two situations, because that is what the server does:
   * hoursModel.buildOrRefreshFromClock creates the sheet when there is none,
   * and otherwise adds only the (job, day) pairs it does not already have,
   * never touching a line somebody edited.
   *
   * Until 2026-09-10 this screen offered it ONLY while `!sheet`. A crew member
   * who built on Monday and worked Tuesday to Friday then had no way to get
   * Tuesday to Friday onto their own sheet — the button was gone, and the
   * only route left was retyping each day by hand. The capability was already
   * on the server; nothing was offering it.
   *
   * The result sentence is the SERVER's `added` count, not a claim. "Timesheet
   * built from your clock" after a top-up that added nothing tells somebody
   * their missing day is on the sheet when it is not.
   */
  const build = () => run('build', async () => {
    const res = await post('/timesheets/from-clock', { week_start: data.start_date });
    const n = Number(res && res.added) || 0;
    setNotice(n
      ? `Added ${n} ${n === 1 ? 'line' : 'lines'} from your clock.`
      : 'Nothing on your clock that is not already on the sheet.');
  });
  const submit = () => run('submit', () => put(`/timesheets/${sheet.id}`, { status: 'submitted' }), 'Submitted for approval.');

  const openEdit = (line) => {
    setEdit(line);
    setEditHours(Number(line.hours) || 0);
    setEditReason(line.adjustment_reason || '');
    setConfirmRemove(false);
  };
  const openAdd = () => {
    setAdding(true);
    setAddDate(data.start_date);
    setAddJob('');
    setAddWhat('');
    setAddHours('');
  };
  const removeLine = async () => {
    if (!edit) return;
    const ok = await run('remove', () => put(`/timesheets/${sheet.id}`, { entries: entriesWithout(lines, edit.id) }), 'Line removed.');
    if (ok) { setEdit(null); setConfirmRemove(false); }
  };
  // A new line needs a name one way or the other: a job picked from the week,
  // or something typed. Hours must be a real number — the server would store
  // NaN as 0 and the worker would never see why their week came up short.
  const addHoursNum = Number(addHours);
  const addValid = !!addDate && addHours !== '' && Number.isFinite(addHoursNum) && addHoursNum > 0
    && (!!addJob || !!addWhat.trim());
  const saveAdd = async () => {
    if (!addValid) return;
    const ok = await run('add', () => put(`/timesheets/${sheet.id}`, {
      entries: entriesWithAdded(lines, {
        job_id: addJob ? Number(addJob) : null,
        date: addDate,
        hours: addHoursNum,
        custom_description: addWhat.trim(),
      }),
    }), 'Line added.');
    if (ok) setAdding(false);
  };
  // Through the ONE rule, not a bare !==. This compared raw floats, so
  // 2.8333333333333335 and 2.8333333333333330 would have read as different
  // hours; the approver's screen rounded to the QUARTER and read a
  // five-minute change as no change at all. Two screens editing the same
  // row under two rules. sameHours is the rule, and hoursModel.js on the
  // server decides the same question the same way.
  // Against the CLOCK, and only the clock. There was a stricter version that
  // also compared to what was already stored on the line, and it is gone on
  // purpose: it existed to stop a snap-at-save moving somebody's pay, and
  // nothing snaps at save any more (see hoursPicker.js snapToGrid). With the
  // stored figure left alone, "does this differ from the clock" is the whole
  // question, and dispatch/lib/format.js reasonRequired asks it the same way
  // so the two screens cannot disagree about when a reason is owed.
  const hoursDiffer = edit && edit.clock_hours != null
    && !sameHours(editHours, edit.clock_hours);
  const reasonMissing = hoursDiffer && !editReason.trim();
  const saveEdit = async () => {
    if (!edit || reasonMissing) return;
    // What is in state — which the PICKER has already snapped if anybody
    // touched it, and which is the line's own stored figure if nobody did.
    // See HoursField: a payroll figure is never moved by opening an editor.
    const n = Number(editHours);
    if (!Number.isFinite(n) || n < 0) { setError('Hours must be a number.'); return; }
    const ok = await run('save', () => put(`/timesheets/${sheet.id}`, { entries: entriesWithEdit(lines, edit.id, n, editReason.trim()) }), 'Line updated.');
    if (ok) setEdit(null);
  };

  let body;
  if (hours.isPending && !data) {
    body = <Skeleton lines={6} />;
  } else if (hours.error && !data) {
    body = (
      <Notice tone="stop" title="Could not load your hours">
        {describeError(hours.error)}{' '}
        <FwButton variant="quiet" onClick={() => hours.refetch()}>Try again</FwButton>
      </Notice>
    );
  } else {
    const days = data.days || [];
    const weeks = data.weeks || [];
    body = (
      <>
        <div className="fd-weeknav">
          <button type="button" className="fd-weeknav__btn" aria-label="Previous week" onClick={() => setWeek(weekBefore(data.start_date))}>
            <Icon name="chevronLeft" />
          </button>
          <div className="fd-weeknav__label">{rangeLabel(data.start_date, data.end_date)}</div>
          <button type="button" className="fd-weeknav__btn" aria-label="Next week" onClick={() => setWeek(weekAfter(data.start_date))}>
            <Icon name="chevronRight" />
          </button>
        </div>
        {/* The week's figure and the week's STATE, together and at the top.
            "Is it approved yet" was answerable only by scrolling past the
            seven day rows to the Timesheet block — and it is the question a
            crew member opens this screen to ask, because an approved week is
            one they can no longer correct. Colour and a WORD (StatusPill), not
            colour alone. */}
        <div className="fd-total">
          <div className="fd-total__figure">
            <span className="fd-total__value fw-num" data-testid="fd-total">{clockLabel(data.total_hours)}</span>
            {/* NOT "hours on the clock": the figure above it is 7:15, and
                "7:15 hours" is the exact confusion between a time and a
                decimal that this screen was changed to stop making. The sheet
                total below says "7:15 on the sheet" — same shape. */}
            <span className="fd-total__label">on the clock</span>
          </div>
          <div className="fd-total__state">
            {sheet ? <StatusPill status={sheet.status} /> : <Pill tone="neutral">No timesheet yet</Pill>}
          </div>
        </div>
        {error ? <Notice tone="stop" onDismiss={() => setError(null)}>{error}</Notice> : null}
        {notice ? <Notice tone="ok" onDismiss={() => setNotice(null)}>{notice}</Notice> : null}

        <div className="fd-section">
          <SectionLabel>Days</SectionLabel>
          {days.length === 0 ? (
            // An empty week is the whole screen, and "No clock time this
            // week" was all it said — so the one person who opens this
            // screen without having clocked in, an admin, saw a blank page
            // and read it as a product with no timesheet in it. That is the
            // owner's report of 2026-09-08 ("Workers should be able to view
            // and modify their own timesheets!!!"): the capability was
            // already here and already worked in a browser
            // (scripts/ui-check.js `worker-timesheet`), and nothing on the
            // empty screen said so. Only when there is nothing to show —
            // a week WITH hours needs no lecture.
            <EmptyState
              icon="clock"
              title="Nothing clocked this week"
              action={<FwButton variant="quiet" onClick={() => navigate('/today')}>Go to Today</FwButton>}
            >
              Clock in on Today and your hours appear here, day by day. Build the
              timesheet from your clock, then open any line to change the hours and
              say why — right up until a manager approves the week.
            </EmptyState>
          ) : days.map((d) => {
            const open = openDay === d.date;
            const dayEntries = d.entries || [];
            // `GET /api/field/hours` adds an entry's hours to the day only
            // once it has an end_time, so a day you are still clocked in on
            // reads 0.00 with a live shift inside it. That number is correct
            // and it looks like "you worked nothing today", so the row says
            // which it is instead of leaving a crew member to work it out.
            const stillRunning = dayEntries.some((e) => e && !e.end_time);
            const jobsLine = dayJobsLabel(dayEntries);
            return (
              <div key={d.date} className="fd-stack">
                <button type="button" className="fd-day" aria-expanded={open ? 'true' : 'false'} onClick={() => setOpenDay(open ? null : d.date)}>
                  <span className="fd-day__main">
                    <span className="fd-day__date">{dayLabel(d.date)}</span>
                    {jobsLine ? <span className="fd-day__jobs">{jobsLine}</span> : null}
                  </span>
                  <span className="fd-day__right">
                    {stillRunning ? <Pill tone="now">Running</Pill> : null}
                    <span className="fd-day__hours fw-num">{clockLabel(d.hours)}</span>
                  </span>
                </button>
                {open ? (
                  <div className="fd-day__entries">
                    {(d.entries || []).map((e) => (
                      <div key={e.id} className="fd-entry">
                        <span>
                          <span className="fd-entry__title">{e.job_title || 'Job'}</span>
                          {e.property_name ? ` · ${e.property_name}` : ''}
                          <br />
                          {timeOf(e.start_time)} – {e.end_time ? timeOf(e.end_time) : 'now'}
                        </span>
                        <span className="fw-num">{e.end_time ? durationLabel(e.duration) : 'Open'}</span>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>

        <div className="fd-sheetblock">
          <div className="fd-sheetblock__head">
            <span className="fd-sheetblock__title">Timesheet</span>
            {sheet ? <StatusPill status={sheet.status} /> : <Pill tone="neutral">No timesheet yet</Pill>}
          </div>
          {/* Sent back: say why, in the approver's words, where the fix is made. */}
          {sheet && sheet.status === 'rejected' ? (
            <Notice tone="warn" data-rejection>
              {sheet.rejection_note ? `Sent back: ${sheet.rejection_note}` : 'Sent back without a note.'} Fix the lines, then submit it again.
            </Notice>
          ) : null}
          {!sheet ? (
            <>
              <FwButton size="lg" full icon="clock" onClick={build} loading={busy === 'build'}>Build from my clock</FwButton>
              <div className="fd-hint" data-clock-action-hint>
                One line per job per day, from finished clock entries this week.
              </div>
            </>
          ) : (
            <>
              <div className="fd-hint">
                {clockLabel(sheet.total_hours)} on the sheet
                {editable ? ' — tap a line to change it.' : ' — approved and locked.'}
              </div>
              {/* ── Top up from the clock, ABOVE the lines ──────────────────
                  Offered on an existing sheet since 2026-09-10; see `build`
                  above for the four unpaid days that were the reason. Above
                  the list rather than under it, for the same reason the
                  manager's copy moved (the owner: "have to scroll all the way
                  to the bottom to see the add from clock") — a week with five
                  days on it is exactly the week you are trying to add the
                  sixth to. */}
              {editable ? (
                <>
                  <FwButton variant="quiet" full icon="clock" onClick={build} loading={busy === 'build'}>
                    Add clock time for this week
                  </FwButton>
                  <div className="fd-hint" data-clock-action-hint>
                    One line per job per day, from finished clock entries this week.
                    Anything already on the sheet is left alone.
                  </div>
                </>
              ) : null}
              <div className="fd-list">
                {lines.map((l) => {
                  const adjusted = isAdjustedLine(l);
                  /* What this line is PRICED at — parity §5.
                     `job_pay_type`/`job_pay_amount` have always ridden in on
                     this response (hoursModel.sheetLines) and nothing drew
                     them, so a crew member on a fixed-price job saw hours and
                     no price. linePriceLabel resolves through the mirror of
                     server/utils/paystubCalc.js and FORMATS — it multiplies
                     nothing, and returns null when the only rate left is the
                     worker's profile rate, which this response does not
                     carry. CLAUDE.md rule 4. */
                  const price = linePriceLabel(l);
                  /* An AMOUNT line has no hours to put in the figure slot.
                     Added 2026-09-10, the owner: "adjustment that has a set
                     amount and not hourly". Such a row is stored `hours: 0`
                     and paid its pay_amount flat (paystubCalc.computeFigures),
                     so this slot printed "0.00" beside a $50 reimbursement —
                     on the screen the person being PAID checks their week on.
                     The amount goes where the hours were, and an Amount pill
                     says which kind of row it is, because "50" read as hours
                     and "50" read as dollars are a payroll error apart.
                     Both come from the resolver the SERVER pays through, so
                     the figure and the label cannot disagree. */
                  const amount = lineAmountLabel(l);
                  const inner = (
                    <>
                      <span className="fd-line__main">
                        <span className="fd-line__job">{shortDate(l.date)} · {l.job_title || l.custom_description || 'Job'}</span>
                        {l.property_name || l.client_name ? <span className="fd-line__meta">{[l.client_name, l.property_name].filter(Boolean).join(' · ')}</span> : null}
                        {amount ? <span className="fd-line__kind"><Pill tone="sched">Amount</Pill></span> : null}
                        {price && !amount ? <span className="fd-line__price">{price}</span> : null}
                      </span>
                      <span className="fd-line__hours fw-num">{amount || clockLabel(l.hours)}</span>
                      {adjusted ? (
                        <span className="fd-line__adj">
                          <span><Pill tone="warn">Adjusted</Pill>{l.clock_hours != null ? <span className="fw-num"> Clock said {clockLabel(l.clock_hours)}</span> : null}</span>
                          {l.adjustment_reason ? <span className="fd-line__reason">{l.adjustment_reason}</span> : null}
                        </span>
                      ) : null}
                    </>
                  );
                  /* The row and its history are SIBLINGS, never nested: the
                     row is a <button> when the week is still editable, and a
                     toggle inside a button is invalid HTML that browsers
                     resolve by dropping one of them. Same reasoning as
                     .fd-jobrow on Today — never a button in a button. */
                  const row = editable ? (
                    <button type="button" className="fd-line" onClick={() => openEdit(l)} aria-label={`Edit ${l.job_title || 'line'} on ${shortDate(l.date)}`}>{inner}</button>
                  ) : (
                    <div className="fd-line">{inner}</div>
                  );
                  return (
                    <div key={l.id} className="fd-linegroup">
                      {row}
                      <LineHistory line={l} />
                    </div>
                  );
                })}
                {lines.length === 0 ? <div className="fd-hint">No lines yet.</div> : null}
              </div>
              {editable ? (
                <FwButton variant="quiet" full icon="plus" onClick={openAdd}>Add a line</FwButton>
              ) : null}
              {(sheet.status === 'draft' || sheet.status === 'rejected') && lines.length > 0 ? (
                <FwButton size="lg" full onClick={submit} loading={busy === 'submit'}>Submit for approval</FwButton>
              ) : null}
            </>
          )}
        </div>

        <Paystubs userId={user && user.id} userName={user && user.name} />

        {weeks.length > 0 ? (
          <div className="fd-section">
            <SectionLabel>Previous weeks</SectionLabel>
            {/* `.fd-weekrow`, NOT `.fd-week`. This row carried `.fd-week`
                until 2026-09-11 and so did Today's seven-cell week strip,
                which is declared later in field.css — so the strip's
                `display: grid; grid-template-columns: repeat(7, minmax(0,1fr))`
                won the cascade and laid these two spans out in the first two
                of seven tracks. At 320px that is about 37px each for a label
                reading "Sep 8 – Sep 14": one word per line, the exact symptom
                docs/APP_REBUILD.md §5.6 is written about. */}
            {weeks.filter((w) => w.start_date !== data.start_date).map((w) => (
              <button key={w.start_date} type="button" className="fd-weekrow" onClick={() => setWeek(w.start_date)}>
                <span>{rangeLabel(w.start_date, w.end_date)}</span>
                <span className="fd-weekrow__right">
                  <span className="fw-num">{clockLabel(w.total_hours)}</span>
                  <StatusPill status={w.status} />
                </span>
              </button>
            ))}
          </div>
        ) : null}

        <Sheet
          open={!!edit}
          title="Change hours"
          onClose={() => setEdit(null)}
          footer={<FwButton size="lg" full onClick={saveEdit} loading={busy === 'save'} disabled={reasonMissing || editHours == null}>Save</FwButton>}
        >
          {edit ? (
            <div className="fd-form">
              <div className="fd-hint">
                {shortDate(edit.date)} · {edit.job_title || 'Job'}
              </div>
              {/* ── A TIME, picked ────────────────────────────────────────
                  Owner, 2026-09-16: "make sure to show a clock or something
                  for them to pick hours, this way we avoid someone
                  misstyping." This was a decimal box beside a hint reading
                  "Clock said 2.75", on a screen that now says 2:45
                  everywhere else. shared/hoursPicker.js has the reading. */}
              <HoursField
                id="fd-edit-hours"
                value={editHours}
                onChange={setEditHours}
                clockHours={edit.clock_hours}
              />
              <FwField
                label="Reason"
                htmlFor="fd-edit-reason"
                required={!!hoursDiffer}
                hint={hoursDiffer ? 'Needed when the hours differ from the clock.' : 'Optional.'}
                error={reasonMissing ? 'Say why the hours differ from the clock.' : null}
              >
                <FwTextarea value={editReason} onChange={(e) => setEditReason(e.target.value)} rows={3} />
              </FwField>
              {/* Taking a line off is a real thing a crew member needs — the
                  clock records a job they opened by mistake and there was no
                  way to undo it. It is also destructive and this is a phone,
                  so it asks once rather than removing on the first tap. */}
              <div className="fd-form__danger">
                {confirmRemove ? (
                  <>
                    <div className="fd-hint">Take this line off the timesheet? The hours go with it.</div>
                    <FwButton variant="stop" full onClick={removeLine} loading={busy === 'remove'}>Yes, remove it</FwButton>
                    <FwButton variant="quiet" full onClick={() => setConfirmRemove(false)}>Keep it</FwButton>
                  </>
                ) : (
                  <FwButton variant="quiet" full icon="trash" onClick={() => setConfirmRemove(true)}>Remove this line</FwButton>
                )}
              </div>
            </div>
          ) : null}
        </Sheet>

        <Sheet
          open={adding}
          title="Add a line"
          onClose={() => setAdding(false)}
          footer={<FwButton size="lg" full onClick={saveAdd} loading={busy === 'add'} disabled={!addValid}>Add line</FwButton>}
        >
          <div className="fd-form">
            <div className="fd-hint">
              For work this week that is not on your clock — a job you forgot to
              start, or something with no job of its own.
            </div>
            <FwField label="Day" htmlFor="fd-add-date">
              <FwSelect id="fd-add-date" value={addDate} onChange={(e) => setAddDate(e.target.value)}>
                {weekDates(data.start_date).map((d) => (
                  <option key={d} value={d}>{dayLabel(d)}</option>
                ))}
              </FwSelect>
            </FwField>
            <FwField label="Job" htmlFor="fd-add-job" hint="Pick the job you actually worked on. Only use “No job” if you have been told to.">
              <FwSelect id="fd-add-job" value={addJob} onChange={(e) => { setAddJob(e.target.value); if (e.target.value) setAddWhat(''); }}>
                <option value="">No job (only if you were told to)</option>
                {weekJobOptions(days, lines).map((o) => (
                  <option key={o.job_id} value={o.job_id}>{o.label}</option>
                ))}
              </FwSelect>
            </FwField>
            {/* Owner instruction, 2026-09-16: a line with no job must be
                warned about IN RED, and the worker told not to reach for it.
                It is not cosmetic — a line with no job_id has no job for
                paystubCalc to take a pay type or rate from
                (paystubCalc.js:107-111), so it pays the worker's hourly rate
                whatever the job was priced at, and the office loses which job
                the hours belong to. Usually right, occasionally very wrong,
                and never what somebody wants by accident. */}
            {!addJob ? (
              <Notice tone="stop" data-hours="no-job-warning">
                <strong>These hours should have an actual job.</strong>{' '}
                Picking a real job is what tells the office which work was paid for, and what pays you the
                right rate when a job is not priced by the hour. Only use “No job” for things like shop time
                or a truck wash — and <strong>never unless you have been told to</strong> by a manager.
              </Notice>
            ) : null}
            {!addJob ? (
              <FwField label="What was it" htmlFor="fd-add-what" required hint="This is what the line is called on your paystub.">
                <FwInput value={addWhat} onChange={(e) => setAddWhat(e.target.value)} placeholder="Truck wash, shop time…" />
              </FwField>
            ) : null}
            {/* Picked, like the edit form — review caught that this one was
                left as a typed decimal, so 2.83 was still enterable on the
                very screen this change is about. */}
            <HoursField id="fd-add-hours" value={Number(addHours) || 0} onChange={setAddHours} required />
          </div>
        </Sheet>
      </>
    );
  }

  return body;
}
